/**
 * Turns the URLs and email addresses inside a plain string into real links.
 *
 * Several places in this app render admin-authored free text verbatim — an
 * Open Play session's notes ("RSVP: https://reclub.co/m/F3KHQ822"), the
 * message of a shared Modal. The admin writes a link expecting it to work;
 * as plain text the guest has to select and copy it by hand on desktop, and
 * on mobile often can't select it at all. This makes those clickable without
 * asking the admin to write markup.
 *
 * Deliberately NOT a markdown or HTML renderer: the input is untrusted
 * admin text rendered into a guest's page, so nothing here can introduce
 * markup. It only ever splits a string and wraps matched runs in <a>, which
 * means the worst a hostile note can do is link somewhere unpleasant — the
 * same as it could by writing the URL out for someone to copy.
 */

// Matched, in order: an explicit http(s) URL, a bare www. host, an email.
//
// The trailing [^\s...] class excludes characters that end a sentence rather
// than belong to the URL — "see https://example.com/rsvp." must not swallow
// the full stop, and "(https://example.com/x)" must not swallow the bracket.
// A URL genuinely ending in one of those is rare enough to be worth losing.
const LINK_PATTERN = /((?:https?:\/\/|www\.)[^\s<>"']*[^\s<>"'.,;:!?)\]}]|[^\s<>"'@]+@[^\s<>"'@]+\.[a-z]{2,})/gi;

/** An email match, as opposed to a URL — decides http:// vs mailto:. */
const isEmail = (text) => /^[^\s@]+@[^\s@]+$/.test(text);

/**
 * @param {string} text - plain text, possibly containing URLs/emails
 * @param {string} [linkClassName] - class applied to each generated <a>
 * @returns {Array<string|JSX.Element>} the string split into text runs and
 *   links, safe to render directly as React children. Returns the input
 *   unchanged (as a single-item array) when it contains no links.
 */
export const linkifyText = (text, linkClassName = 'app-inline-link') => {
  const source = String(text ?? '');
  if (!source) return [source];

  const parts = [];
  let lastIndex = 0;
  // A /g regex carries state between calls, so it is reset per invocation
  // rather than shared — otherwise the second call on the same string would
  // start mid-way through and silently miss the first link.
  LINK_PATTERN.lastIndex = 0;

  let match = LINK_PATTERN.exec(source);
  while (match !== null) {
    if (match.index > lastIndex) parts.push(source.slice(lastIndex, match.index));

    const raw = match[0];
    const email = isEmail(raw);
    // A bare "www.foo.com" has no scheme, and href-ing it as-is would
    // resolve relative to the current page ("/book/www.foo.com").
    const href = email ? `mailto:${raw}` : (/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);

    parts.push(
      <a
        key={`${match.index}-${raw}`}
        className={linkClassName}
        href={href}
        // Opening in a new tab keeps a guest mid-checkout from losing their
        // place; noreferrer/noopener because the destination is whatever an
        // admin typed and must not get a handle on this window.
        {...(email ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
      >
        {raw}
      </a>
    );

    lastIndex = match.index + raw.length;
    match = LINK_PATTERN.exec(source);
  }

  if (lastIndex < source.length) parts.push(source.slice(lastIndex));
  return parts;
};

export default linkifyText;
